import React, { Component } from 'react';

// Actions
import {
  prepareArticles,
  prepareConversationArticles,
  prepareTrendingArticles,
  prepareVideoArticles,
  stripStartingAndEndingPTags,
} from './vantage.actions';

// Utilities
import { createOmnitureDataLayer, omnitureTriggerEventAction } from '../../utilities/omniture-data-layer';
import { Desktop, Mobile } from '../../app-responsive';

// Constants
import { PAGE_TYPE_FOLLOWUP, PAGE_TYPE_GENERIC, PAGE_TYPE_TRIPREPORT, PAGE_TYPE_UNATTENDED } from '../../app-constants';

// Components
import EventsAppWrapper from '../../events-app-wrapper';
import { EventCardWrapper } from './event-components/cards';
import { EventRowHeader, EventsRow } from './event-components/layout';
import Article from './event-components/article';
import EventCarousel from './event-components/carousel';
import { ConversationArticle, HighlightsArticle } from './event-components/content-types';
import VideoModal from './event-components/video-modal';
import TripReport from '../../components/trip-report/trip-report';
import Welcome from '../../components/welcome/welcome';
import YoutubeVideo from './event-components/youtube-video';
import GenericErrorModal from '../../components/generic-error-modal';

import './vantage.css';
import EventFloatingCta from './event-components/event-floating-cta';

class VantageView extends Component {
  static HighLights = ({
    highlightsTitle, trendingArticlesList, highlightsReadMoreText,
    highlightsWatchVideoText, eventsTrackResourceViews, relatedContentHeadingText, uId, highlightsViewMoreText,
  }) => (
    <EventsRow wrapperName="highlights" automatedTestingId="highlights-wrapper">
      <EventRowHeader headingPriority="2" title={highlightsTitle} />
      <EventCardWrapper
        contentToMap={prepareTrendingArticles(trendingArticlesList, highlightsReadMoreText,
          highlightsWatchVideoText, eventsTrackResourceViews, relatedContentHeadingText, uId)}
        RepeatableInjectedComponent={HighlightsArticle}
        cardsPerRow={2}
        numberInitialRowsToShow={2}
        numberRowsToShowOnClick={1}
        viewMoreText={highlightsViewMoreText}
        cardClass="card-body"
        collapseSpacingOnMobile
        truncateSummary
      />
    </EventsRow>
  );

  static Videos = ({ videosTitle, videoList, videosViewMoreText, eventsTrackResourceViews, relatedContentHeadingText, uId }) => (
    <EventsRow wrapperName="video" automatedTestingId="video-wrapper">
      <EventRowHeader headingPriority="2" title={videosTitle} />
      <EventCardWrapper
        contentToMap={prepareVideoArticles(videoList, eventsTrackResourceViews, relatedContentHeadingText, uId)}
        RepeatableInjectedComponent={VideoModal}
        cardsPerRow={3}
        numberRowsToShowOnClick={1}
        viewMoreText={videosViewMoreText}
      />
    </EventsRow>
  );

  static Articles = ({
    articlesTitle,
    articleList,
    eventsTrackResourceViews,
    uId,
    articlesViewMoreText,
    articlesReadMoreText,
  }) => (
    <EventsRow wrapperName="associated-articles" automatedTestingId="articles-wrapper">
      <EventRowHeader headingPriority="2" title={articlesTitle} />
      <EventCardWrapper
        contentToMap={prepareArticles(articleList, eventsTrackResourceViews, uId)}
        RepeatableInjectedComponent={Article}
        cardsPerRow={3}
        numberRowsToShowOnClick={2}
        viewMoreText={articlesViewMoreText}
        cardClass="card-body"
        buttonText={articlesReadMoreText}
        collapseSpacingOnMobile
        truncateSummary
      />
    </EventsRow>
  );

  constructor(props) {
    super(props);
    const { pageType, authorableContent: { welcome: { [pageType]: { documentTitle = '' } } } } = props;
    document.title = documentTitle;
  }

  componentDidMount() {
    const { pageType, uId } = this.props;
    createOmnitureDataLayer(pageType, uId);
    omnitureTriggerEventAction('pageView');
  }

  handleCallbackRequest = () => {
    const {
      uId,
      pageType,
      selectedSlides,
      eventId,
      actions: {
        sendCallbackRequest,
      },
    } = this.props;

    sendCallbackRequest({
      userId: uId,
      pageId: pageType,
      selectedSlidesList: selectedSlides,
    },
    eventId);
  };

  render() {
    const {
      authorableContent,
      vantage,
      uId,
      eventId,
      pageType,
      downloadTripReportIsLoading,
      emailTripReportIsSending,
      selectedSlides,
      showGenericErrorModal,
    } = this.props;
    const {
      tripReport,
      shareReport,
      highlights: {
        title: highlightsTitle, readMoreText: highlightsReadMoreText, watchVideoText: highlightsWatchVideoText, viewMoreText: highlightsViewMoreText,
      },
      videos: {
        [pageType]: { title: videosTitle } = { title: '' }, // default value for computed pageType
        viewMoreText: videosViewMoreText,
        relatedContentHeadingText,
      },
      discover: {
        title: discoverTitle, content: discoverContent,
      },
      articles: {
        [pageType]: { title: articlesTitle } = { title: '' }, // default value for computed pageType
        readMoreText: articlesReadMoreText,
        viewMoreText: articlesViewMoreText,
      },
      conversation: {
        title: conversationTitle,
        content: conversationContent,
        description: conversationDescription,
        customContent: conversationCustomContent,
      },
      welcome: {
        [pageType]: {
          title: welcomeTitle = '',
          preStateDescriptionText = '',
          postStateDescriptionText = '',
        },
        headerImageMobile, headerImageDesktop, headerImageAltText, greeting,
      },
      floatingCta,
      highlightsVideo: {
        [pageType]: { title: highlightsReelTitle } = { title: '' }, // default value for computed pageType
        youtubeVideoTitle: highlightsYoutubeVideoTitle,
        id: highlightsYoutubeVideoId,
      },
      genericErrorModal,
    } = authorableContent;
    const {
      videoList,
      trendingList: trendingArticlesList,
      articleList,
      tripReport: tripReportSlides,
      firstName,
      state,
      callbackRequested,
    } = vantage;
    const {
      startEventDownloadPdfProcess,
      emailEventReport,
      eventsTrackResourceViews,
      updateSelectedSlides,
      hideGenericErrorModal,
      openCallbackRequestModal,
    } = this.props.actions;
    const Highlights = (
      <VantageView.HighLights
        highlightsTitle={highlightsTitle}
        trendingArticlesList={trendingArticlesList}
        highlightsReadMoreText={highlightsReadMoreText}
        highlightsWatchVideoText={highlightsWatchVideoText}
        eventsTrackResourceViews={eventsTrackResourceViews}
        relatedContentHeadingText={relatedContentHeadingText}
        uId={uId}
        highlightsViewMoreText={highlightsViewMoreText}
      />
    );
    const Videos = (
      <VantageView.Videos
        VideoModal={VideoModal}
        videosTitle={videosTitle}
        videoList={videoList}
        videosViewMoreText={videosViewMoreText}
        eventsTrackResourceViews={eventsTrackResourceViews}
        relatedContentHeadingText={relatedContentHeadingText}
        uId={uId}
      />
    );
    const Articles = (
      <VantageView.Articles
        articlesTitle={articlesTitle}
        articleList={articleList}
        eventsTrackResourceViews={eventsTrackResourceViews}
        uId={uId}
        articlesViewMoreText={articlesViewMoreText}
        articlesReadMoreText={articlesReadMoreText}
      />
    );
    return (
      <EventsAppWrapper>
        <div className="events">
          {pageType !== PAGE_TYPE_GENERIC && (<EventFloatingCta
            openModal={openCallbackRequestModal}
            cta={floatingCta}
            callbackRequestComplete={callbackRequested}
          />) }
          <div className="container-fluid">
            <div className="row">
              <div className="col px-0 mb-5 banner-image">
                <Mobile>
                  <img src={headerImageMobile} alt={headerImageAltText} />
                </Mobile>
                <Desktop>
                  <img src={headerImageDesktop} alt={headerImageAltText} className="img-fluid" />
                </Desktop>
              </div>
            </div>
          </div>
          <div className="root-container container">
            <EventsRow wrapperName="welcome" automatedTestingId="welcome-wrapper">
              <EventRowHeader headingPriority="1" title={stripStartingAndEndingPTags(welcomeTitle)} />
              <EventCardWrapper>
                <Welcome
                  customerName={firstName}
                  state={state}
                  preStateDescriptionText={stripStartingAndEndingPTags(preStateDescriptionText)}
                  postStateDescriptionText={stripStartingAndEndingPTags(postStateDescriptionText)}
                  greeting={greeting}
                />
              </EventCardWrapper>
            </EventsRow>
            <EventsRow wrapperName="highlights-reel" automatedTestingId="highlights-reel-wrapper">
              <EventRowHeader headingPriority="2" title={highlightsReelTitle} />
              <EventCardWrapper>
                <YoutubeVideo
                  youtubeVideoTitle={highlightsYoutubeVideoTitle}
                  id={highlightsYoutubeVideoId}
                  autoplay={false}
                />
              </EventCardWrapper>
            </EventsRow>
            {tripReportSlides &&
            <EventsRow wrapperName="trip-report" automatedTestingId="tripreport-wrapper">
              <EventRowHeader headingPriority="2" title={tripReport.title} />
              <EventCardWrapper>
                <TripReport
                  tripReport={tripReport}
                  uId={uId}
                  eventId={eventId}
                  downloadEventReportAction={startEventDownloadPdfProcess}
                  shareReport={shareReport}
                  tripReportSlides={tripReportSlides}
                  sendEmailAction={emailEventReport}
                  downloadTripReportIsLoading={downloadTripReportIsLoading}
                  emailTripReportIsSending={emailTripReportIsSending}
                  selectedSlides={selectedSlides}
                  updateSelectedSlides={updateSelectedSlides}
                  showGenericErrorModal={showGenericErrorModal}
                />
              </EventCardWrapper>
            </EventsRow>
            }
            {(pageType === PAGE_TYPE_TRIPREPORT || pageType === PAGE_TYPE_GENERIC) &&
            <React.Fragment>
              {trendingArticlesList.length > 0 && Highlights}
              {videoList.length > 0 && Videos}
            </React.Fragment>
            }
            {(pageType === PAGE_TYPE_FOLLOWUP || pageType === PAGE_TYPE_UNATTENDED) &&
            <React.Fragment>
              {videoList.length > 0 && Videos}
              {articleList.length > 0 && Articles}
              {trendingArticlesList.length > 0 && Highlights}
            </React.Fragment>
            }
            <EventsRow wrapperName="conversation" automatedTestingId="conversation-wrapper">
              <EventRowHeader headingPriority="3" title={conversationTitle} description={conversationDescription} />
              <EventCardWrapper
                contentToMap={prepareConversationArticles(
                  {
                    articles: conversationContent,
                    customContent: conversationCustomContent,
                    callbackRequested,
                    callbackRequest: this.handleCallbackRequest,
                    uId,
                  },
                )}
                RepeatableInjectedComponent={ConversationArticle}
                cardsPerRow={uId ? 3 : 2}
                callbackRequest={this.handleCallbackRequest}
                doNotIncludeMarginBottom
              />
            </EventsRow>
            <EventsRow wrapperName="discover" automatedTestingId="discover-wrapper">
              <EventRowHeader headingPriority="3" title={discoverTitle} />
              <EventCardWrapper>
                <EventCarousel
                  contentToMap={discoverContent}
                  cardsPerSlideDesktop={3}
                  cardsPerSlideMobile={1}
                  mobile
                  desktop
                />
              </EventCardWrapper>
            </EventsRow>
          </div>
        </div>
        <GenericErrorModal
          showGenericErrorModal={showGenericErrorModal}
          hideGenericErrorModal={hideGenericErrorModal}
          genericErrorModal={genericErrorModal}
        />
      </EventsAppWrapper>
    );
  }
}

export default VantageView;
